import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText } from "lucide-react";
import type { PolicyTemplate } from "@/lib/template-utils";
import ReactMarkdown from "react-markdown";

interface TemplatePreviewDialogProps {
  template: PolicyTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUse: (template: PolicyTemplate) => void;
}

export function TemplatePreviewDialog({
  template,
  open,
  onOpenChange,
  onUse,
}: TemplatePreviewDialogProps) {
  if (!template) return null;

  const handleUse = () => {
    onUse(template);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {template.title}
          </DialogTitle>
          <DialogDescription>{template.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 min-h-0 flex flex-col">
          {/* Metadata */}
          <div className="flex flex-wrap gap-2">
            {template.category && (
              <Badge variant="outline">{template.category}</Badge>
            )}
            {template.compliance_framework && (
              <Badge variant="secondary">{template.compliance_framework}</Badge>
            )}
            {template.template_type === 'global' && (
              <Badge variant="default">Global Template</Badge>
            )}
            {template.template_type === 'internal' && (
              <Badge variant="outline">Internal Template</Badge>
            )}
          </div>

          {/* Content Preview */}
          <ScrollArea className="flex-1 border rounded-lg p-4">
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown>{template.content}</ReactMarkdown>
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleUse}>
            Use This Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

